import { compact, map } from 'lodash';
import {
  formatEpochAsDate,
  formatEpochAsDateTime,
  formatEpochAsTime
} from '../../util/datetime';
import { extractExcerpt, replaceTags, sanitizeHtml, toUUID } from '../../util/transform';
import Document from '../document/Document';
import { DocType } from '../global/Doc.enum';
import Root from '../Root';
import { NoteType } from './Note.enum';
import type { INote } from './Note.interface';
import NoteAttribute from './NoteAttribute';
import NoteOwner from './NoteOwner';

export default class Note extends Root implements INote {
  uuid: string;
  idWorkflow: string;
  idTicket: string;
  idContact: string;
  idOwnerContact: string;
  idOwnerPartner: string;
  idLabels: Array<string>;
  owner: NoteOwner;
  type: string;
  content: string;
  attributes: Array<NoteAttribute>;
  attachments: Array<Document>;
  isSharedPartner: boolean;
  isShared: boolean;
  isPinned: boolean;

  getName() {
    return this.name;
  }

  getExcerpt() {
    return extractExcerpt(replaceTags(this.getContent(), ' '));
  }

  getContent() {
    return this.content;
  }

  hasShowMore() {
    const contentsArray = this.content.split('\n');
    const lettersCount = this.content.replace(/[^0-9a-zA-Z]/gi, '').length;
    return contentsArray.length > 14 || lettersCount > 1024;
  }

  getType() {
    return this?.type?.title() || this.type || 'N/A';
  }

  getIsImportant() {
    return this.isPinned;
  }

  hasAttachment() {
    return this.attachments.length > 0;
  }

  hasLabel() {
    return this.idLabels.length > 0;
  }

  getIsShared() {
    return this.isShared;
  }

  getIsSharedPartner() {
    return this.isSharedPartner;
  }

  getDateSort() {
    return this.timeStamp;
  }

  getDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.timeStamp, timeZone, locale, localeFormat);
  }

  getTime(timeZone, locale, localeFormat) {
    return formatEpochAsTime(this.timeStamp, timeZone, locale, localeFormat);
  }

  getDateTime(timeZone, locale, localeFormat) {
    return formatEpochAsDateTime(this.timeStamp, timeZone, locale, localeFormat);
  }

  static getAttributeDateTime = (timeStamp, timeZone, locale, localeFormat) =>
    formatEpochAsDateTime(timeStamp, timeZone, locale, localeFormat);

  static getAttributeVideoDateTime = (attribute, timeZone, locale, localeFormat) => {
    let out = '';
    if (attribute.name === 'Started' || attribute.name === 'Finished') {
      out = formatEpochAsDateTime(attribute.value, timeZone, locale, localeFormat);
    }
    return out;
  };

  static build = (data: INote) => new Note(data);
  static blank = () => new Note({ isActive: true });

  /**
   * Constructor
   * @param {INote} data
   */
  constructor(data?: INote) {
    super(DocType.of('__datum.note'), data);

    this.uuid = toUUID(data?.uuid);
    this.idWorkflow = toUUID(data?.idWorkflow);
    this.idTicket = toUUID(data?.idTicket);
    this.idContact = toUUID(data?.idContact);
    this.idOwnerContact = toUUID(data?.idOwnerContact);
    this.idOwnerPartner = toUUID(data?.idOwnerPartner);
    this.idLabels = compact(map(data?.idLabels, (obj) => toUUID(obj)));
    this.owner = new NoteOwner(data?.owner);
    this.type = NoteType.of(data?.type?.name || data?.type);
    this.content = sanitizeHtml(data?.content || '');
    this.attributes = map(data?.attributes, (obj) => new NoteAttribute(obj));
    this.attachments = map(data?.attachments, (obj) => new Document(obj));
    this.isSharedPartner = data?.isSharedPartner === true;
    this.isShared = data?.isShared === true;
    this.isPinned = data?.isPinned === true;
  }
}
